import { create } from "zustand";
import Cookies from "js-cookie";
import { ChargeBeeProductDetails } from "~/types/Payment";
import { STORAGE_KEYS } from "~/types/Storage";
import { COOKIES } from "~/types/Cookie";
import { getStorageItem, setStorageItem } from "~/lib/storage/Storage";
import type { LogEvent } from "~/hooks/useLogEvent";
import { allowlistedErrorName } from "~/utils/errorTelemetry";
import {
    CHECKOUT_COMPONENT,
    CHECKOUT_CUSTOMER_IMPACT,
    CHECKOUT_FAILURE_BOUNDARY,
    CHECKOUT_FAILURE_CATEGORY,
    CHECKOUT_LOG_EVENT,
    CHECKOUT_RESULT,
    CHECKOUT_STAGE,
    logCheckoutEvent,
} from "~/app/checkout/utils/checkoutInstrumentation";

/**
 * What is wrong with the persisted cart, as seen at checkout mount. When the
 * cart does not survive, `priceItems` comes out empty and PURCHASE does
 * nothing; `checkout_submit_blocked` reports the dead button, these say why.
 *
 * Reported at `warn`: a cleared or unwritable `localStorage` is the customer's
 * browser — private mode, an exhausted quota, an extension — not a defect.
 */
const CART_STORAGE_ISSUE = {
    /** The stored cart could not be parsed. */
    CORRUPTED: "corrupted",
    /**
     * The cookie remembers slugs storage no longer has: either a write never
     * persisted, or storage was cleared under us.
     */
    LOST: "lost",
} as const;

type CartStorageIssue =
    (typeof CART_STORAGE_ISSUE)[keyof typeof CART_STORAGE_ISSUE];

type CartState = {
    cartItems: ChargeBeeProductDetails[];
    addCartItems: (product: ChargeBeeProductDetails) => void;
    setProducts: (items: ChargeBeeProductDetails[]) => void;
    removeProduct: (identifier: string) => void;
    removeAllProducts: () => void;
    replaceSelectedPriceItem: (
        slug: string,
        selectedPriceItemId: string,
        upgradedFromPriceItemId?: string
    ) => void;
    totalPrice: number;
};

const COOKIE_MAX_AGE_DAYS = 30;

export const syncCartCookie = (items: ChargeBeeProductDetails[]) => {
    if (typeof document === "undefined") return;
    const slugs = items.map((item) => item.slug).filter((s): s is string => !!s).join(",");
    if (slugs) {
        Cookies.set(COOKIES.CART_SLUGS, slugs, {
            path: "/",
            expires: COOKIE_MAX_AGE_DAYS,
            sameSite: "Lax",
        });
    } else {
        Cookies.remove(COOKIES.CART_SLUGS, { path: "/" });
    }
};

function getCookieValue(name: string): string | undefined {
    if (typeof document === "undefined") return undefined;
    return Cookies.get(name) || undefined;
}

/**
 * The store's way to reach Datadog. Writes happen inside Zustand actions,
 * which have no hook context and no caller willing to carry a logger down to
 * them, so the mount-time reconciliation below lends it its own `LogEvent`.
 * `null` until then; a write that beats the mount falls back to
 * `console.error`.
 */
let cartLogEvent: LogEvent | null = null;

/**
 * Reconciles cookie and localStorage on client init.
 *
 * Rewrites the cookie whenever the set of slugs differs from localStorage,
 * so server-side resolveCartSlugs always reads up-to-date values.
 *
 * `logEvent` is required rather than optional: this is the one moment we can
 * see that the cart did not survive.
 */
export const reconcileCookieWithStorage = (logEvent: LogEvent) => {
    if (typeof document === "undefined") return;

    // Lends the logger to `saveToStorage`, which cannot be handed one. Before
    // the early returns, so an in-sync cart can still report write failures.
    cartLogEvent = logEvent;

    const cookieValue = getCookieValue(COOKIES.CART_SLUGS);
    let storageItems: Array<{ slug?: string }> = [];
    let issue: CartStorageIssue | undefined;
    let errorName: string | undefined;

    try {
        const raw = getStorageItem(STORAGE_KEYS.CART_STORAGE_KEY);
        if (raw) {
            const parsed: unknown = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                storageItems = parsed;
            } else {
                // Valid JSON, but not the array this key is supposed to hold.
                issue = CART_STORAGE_ISSUE.CORRUPTED;
            }
        }
    } catch (error) {
        // corrupted storage — clear cookie to match
        issue = CART_STORAGE_ISSUE.CORRUPTED;
        errorName = allowlistedErrorName(error);
    }

    const storageSlugs = storageItems
        .map((i) => i.slug)
        .filter((s): s is string => !!s);

    const cookieSlugs = cookieValue
        ? cookieValue.split(",").map((s) => s.trim()).filter(Boolean)
        : [];

    const storageSet = new Set(storageSlugs);
    const cookieSet = new Set(cookieSlugs);
    const inSync =
        storageSet.size === cookieSet.size &&
        storageSlugs.every((s) => cookieSet.has(s));

    // Only when the cookie remembers something storage does not; the reverse is
    // the ordinary case this function exists to fix.
    if (!issue && cookieSlugs.some((slug) => !storageSet.has(slug))) {
        issue = CART_STORAGE_ISSUE.LOST;
    }

    // Reconciliation first, deliberately: a `logEvent` that threw must not be
    // able to leave the cookie unreconciled.
    if (!inSync) {
        syncCartCookie(storageSlugs.map((slug) => ({ slug }) as ChargeBeeProductDetails));
    }

    if (issue) {
        // `LogEvent`'s no-throw guarantee is not enforceable on a caller who
        // passes a plain function of the same shape. This is the first
        // statement of `useGetInitialProducts`' mount effect, so an escaping
        // throw would blank the checkout page and abandon the URL cart seeding.
        try {
            logCheckoutEvent(logEvent, {
                message: CHECKOUT_LOG_EVENT.CART_STORAGE_UNUSABLE,
                level: "warn",
                data: {
                    result: CHECKOUT_RESULT.FAILURE,
                    stage: CHECKOUT_STAGE.STORAGE,
                    component: CHECKOUT_COMPONENT.CART_STORAGE,
                    failureBoundary: CHECKOUT_FAILURE_BOUNDARY.STORAGE,
                    failureCategory: CHECKOUT_FAILURE_CATEGORY.STORAGE,
                    reason: issue,
                    customerImpact:
                        CHECKOUT_CUSTOMER_IMPACT.CHECKOUT_BLOCKED,
                    issue,
                    errorName,
                    // Counts only: the slugs are already reported by the
                    // checkout funnel, and the shape of the mismatch is enough.
                    cookieSlugCount: cookieSlugs.length,
                    storageSlugCount: storageSlugs.length,
                },
            });
        } catch {
            // Deliberately empty; a lost breadcrumb beats a dead page.
        }
    }
};

/**
 * The cart could not be turned into JSON. Not a storage failure:
 * `setStorageItem` never throws and reports a refused write as
 * `persisted: false`, so the only way in is a cart item holding a value
 * `JSON.stringify` refuses — a `BigInt`, a circular reference, a throwing
 * getter — which means a product shape we built wrong. Hence `error`.
 *
 * The cart is then left unpersisted while the cookie still names its slugs, so
 * the next load reports the same incident as `lost`.
 */
const reportUnserializableCart = (error: unknown, itemCount: number) => {
    try {
        // Runs inside a Zustand action, so a throw here would abort the `set`
        // that follows and leave the store mid-update.
        if (cartLogEvent) {
            logCheckoutEvent(cartLogEvent, {
                message: CHECKOUT_LOG_EVENT.CART_ITEMS_UNSERIALIZABLE,
                level: "error",
                data: {
                    result: CHECKOUT_RESULT.FAILURE,
                    stage: CHECKOUT_STAGE.STORAGE,
                    component: CHECKOUT_COMPONENT.CART_STORAGE,
                    failureBoundary: CHECKOUT_FAILURE_BOUNDARY.CLIENT,
                    failureCategory: CHECKOUT_FAILURE_CATEGORY.DATA,
                    reason: "items_unserializable",
                    customerImpact:
                        CHECKOUT_CUSTOMER_IMPACT.CHECKOUT_BLOCKED,
                    errorName: allowlistedErrorName(error),
                    itemCount,
                },
            });
        }
    } catch {
        // Deliberately empty; see the no-throw note on `LogEvent`.
    }

    if (!cartLogEvent) {
        console.error("Cart items could not be serialized for storage", {
            errorName: allowlistedErrorName(error),
            itemCount,
        });
    }
};

const saveToStorage = (items: ChargeBeeProductDetails[]) => {
    try {
        const minimal = items.map((item) => ({
            slug: item.slug,
            priceItemId: item.priceItemId,
            selectedPriceItemId: item.selectedPriceItemId,
            upgradedFromPriceItemId: item.upgradedFromPriceItemId,
        }));
        setStorageItem(
            STORAGE_KEYS.CART_STORAGE_KEY,
            JSON.stringify(minimal)
        );
    } catch (error) {
        reportUnserializableCart(error, items.length);
    }
    syncCartCookie(items);
};

export const useCartStore = create<CartState>((set, get) => {
    const itemPrice = (p: ChargeBeeProductDetails) => {
        const items = p.priceItems ?? [];

        // if only one priceItem — use it
        if (items.length === 1) {
            const only = items[0]?.price;
            return typeof only === "number" ? only : p.price;
        }

        // if selectedPriceItemId is set — look for a match by id or slug
        if (p.selectedPriceItemId && items.length > 0) {
            const match = items.find(
                (pi) =>
                    pi.id === p.selectedPriceItemId ||
                    pi.slug === p.selectedPriceItemId
            )?.price;
            if (typeof match === "number") return match;
        }

        // if no match found, return the first priceItem if it exists
        const first = items[0]?.price;
        if (typeof first === "number") return first;

        // final fallback to product.price
        return p.price;
    };

    const selectedPriceItem = (p: ChargeBeeProductDetails) => {
        const items = p.priceItems ?? [];

        if (items.length === 1) {
            return items[0];
        }

        // if selectedPriceItemId is set — look for a match by id or slug
        if (p.selectedPriceItemId && items.length > 0) {
            const match = items.find(
                (pi) =>
                    pi.id === p.selectedPriceItemId ||
                    pi.slug === p.selectedPriceItemId
            );
            if (match) return match;
        }

        // if no match found, return the first priceItem if it exists
        return items[0];
    };

    const getTotalPrice = (items) => {
        const total = items.reduce((acc, p) => acc + itemPrice(p), 0) / 100;

        // Apply 10% discount if there are two or more products
        // const discounted = items.length > 1 ? total * 0.9 : total;
        // return Math.round((discounted + Number.EPSILON) * 100) / 100;
        return total;
    };

    return {
        cartItems: [],
        totalPrice: 0,

        setProducts: (items) => {
            set({
                cartItems: items.map((item) => ({
                    ...item,
                    selectedPriceItem: selectedPriceItem(item),
                })),
                totalPrice: getTotalPrice(items),
            });
            saveToStorage(items);
        },

        addCartItems: (product) => {
            const enrichedProduct = {
                ...product,
                selectedPriceItem: selectedPriceItem(product),
            };

            const newItems = [enrichedProduct];

            set({
                cartItems: newItems,
                totalPrice: getTotalPrice(newItems),
            });
            saveToStorage(newItems);
        },

        removeProduct: (identifier) => {
            // identifier can be slug or priceItemId
            const newItems = get().cartItems.filter((p) => {
                return identifier !== p.slug && identifier !== p.priceItemId;
            });
            set({
                cartItems: newItems,
                totalPrice: getTotalPrice(newItems),
            });
            saveToStorage(newItems);
        },

        replaceSelectedPriceItem: (slug, selectedPriceItemId, upgradedFromPriceItemId) => {
            const newItems = get().cartItems.map((item) => {
                if (item.slug !== slug) return item;

                const updatedItem = {
                    ...item,
                    selectedPriceItemId,
                    upgradedFromPriceItemId,
                };

                return {
                    ...updatedItem,
                    selectedPriceItem: selectedPriceItem(updatedItem),
                };
            });

            set({
                cartItems: newItems,
                totalPrice: getTotalPrice(newItems),
            });
            saveToStorage(newItems);
        },

        removeAllProducts: () => {
            set({
                cartItems: [],
                totalPrice: 0,
            });
            saveToStorage([]);
        },

        // totalPrice is a derived value kept in-sync above
    };
});